import React, { useEffect, useState, useCallback } from "react";
import axios from "../../../../utils/cmsAxios";
import { useNavigate, useParams } from "react-router-dom";
import ConfirmModal from "../../CommonComponents/ConfirmModal";

const AdminDetail = () => {
  const navigate = useNavigate();
  const { id } = useParams();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [openDelete, setOpenDelete] = useState(false);
  const [openAlert, setOpenAlert] = useState(false);
  const [alertMsg, setAlertMsg] = useState("");

  // 폼 상태
  const [isActive, setIsActive] = useState(true);
  const [role, setRole] = useState("STAFF");
  const [name, setName] = useState("");
  const [username, setUsername] = useState(""); // 읽기전용

  const [phone, setPhone] = useState("");
  const [email, setEmail] = useState("");
  const [department, setDepartment] = useState("");
  const [position, setPosition] = useState("");

  // 읽기전용 메타
  const [, setLastLoginAt] = useState("");
  const [, setVisitCount] = useState(0);
  const [, setCreatedAt] = useState("");
  const [, setCreatedBy] = useState("admin");

  const load = useCallback(async () => {
    try {
      setLoading(true);
      setError("");
      const { data } = await axios.get(`${process.env.REACT_APP_FASTAPI_ADS_URL}/admin/${id}`);
      // 서버 필드명에 맞춰 매핑
      setIsActive(Number(data.is_active) === 1);
      setRole(data.role || "STAFF");
      setName(data.name || "");
      setUsername(data.username || data.admin_uid || "");
      setPhone(data.phone || "");
      setEmail(data.email || "");
      setDepartment(data.department || "");
      setPosition(data.position || "");
      setLastLoginAt(data.last_login_at || "");
      setVisitCount(data.visit_count ?? 0);
      setCreatedAt(data.created_at || "");
      setCreatedBy(data.created_by || "admin");
    } catch (e) {
      console.error(e);
      setError("관리자 정보를 불러오지 못했습니다.");
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => { load(); }, [load]);

  const save = async (e) => {
    e.preventDefault();

    setSaving(true);
    setError("");

    const payload = {
      is_active: isActive ? 1 : 0,
      role,
      name: name.trim() || null,
      phone: phone.trim() || null,
      email: email.trim() || null,
      department: department.trim() || null,
      position: position.trim() || null,
    }

    console.log(payload)

    try {
      await axios.post(`${process.env.REACT_APP_FASTAPI_ADS_URL}/admin/update/${id}`, 
        payload
      );
      setAlertMsg("저장이 완료되었습니다.");
      setOpenAlert(true);
    } catch (e) {
      console.error(e);
      setError("저장에 실패했습니다.");
    } finally {
      setSaving(false);
    }
  };

  const remove = async () => {
    try {
      await axios.delete(`${process.env.REACT_APP_FASTAPI_ADS_URL}/admin/delete/${id}`);
      navigate("/admin/list", { replace: true });
    } catch (e) {
      console.error(e);
      setError("삭제 중 오류가 발생했습니다.");
    } finally {
      setOpenDelete(false);
    }
  };

  const goList = () => navigate("/admin/list", { replace: true });

  return (
    <div className="min-h-screen bg-neutral-50">
      <div className="mx-auto max-w-[1280px] bg-white rounded-2xl shadow overflow-hidden">
        <div className="flex">

          <div className="flex-1 min-w-0 p-4 bg-neutral-50/70">
            <main className="pt-12 flex flex-col p-4 overflow-x-hidden w-full">
              <section className="p-4">
                <div className="mb-3 flex items-center justify-between">
                  <h2 className="text-xl font-semibold mb-4">관리자 계정 권한 관리 &gt; 상세(수정)</h2>
                </div>

                {error && (
                  <div className="mb-3 rounded-lg bg-red-50 text-red-700 px-3 py-2 text-sm">{error}</div>
                )}
                {loading ? (
                  <div className="p-10 text-center text-gray-500">불러오는 중…</div>
                ) : (
                  <form onSubmit={save} className="space-y-4 max-w-full" autoComplete="on">
                    <h2 className="pb-3 font-bold">기본 정보</h2>
                    {/* 기본 정보 카드 */}                    
                    <div className="rounded-lg border bg-white">                      
                      <div className="p-4">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-3 text-sm">
                          <div className="flex items-center gap-3">
                            <div className="w-28 shrink-0 text-neutral-500">사용 여부</div>
                            <div className="flex items-center gap-6">
                              <label className="inline-flex items-center gap-2">
                                <input type="radio" name="active" checked={isActive} onChange={() => setIsActive(true)} />
                                <span>Yes</span>
                              </label>
                              <label className="inline-flex items-center gap-2">
                                <input type="radio" name="active" checked={!isActive} onChange={() => setIsActive(false)} />
                                <span>No</span>
                              </label>
                            </div>
                          </div>

                          <div className="flex items-center gap-3">
                            <div className="w-28 shrink-0 text-neutral-500">관리자 유형</div>
                            <select
                              value={role}
                              onChange={(e) => setRole(e.target.value)}
                              className="h-9 w-full rounded-md border border-neutral-300 bg-white px-2 text-sm"
                            >
                              <option value="SUPER">최고 관리자</option>
                              <option value="MANAGER">중간 관리자</option>
                              <option value="STAFF">일반 관리자</option>
                            </select>
                          </div>

                          <L label="이름"><I name="name" autoComplete="name" value={name} onChange={e=>setName(e.target.value)} /></L>
                          <L label="관리자 ID"><I name="username" autoComplete="username" value={username} disabled /></L>
                          <L label="휴대폰 번호"><I name="tel" autoComplete="tel" value={phone} onChange={e=>setPhone(e.target.value)} /></L>
                          <L label="이메일"><I name="email" autoComplete="email" value={email} onChange={e=>setEmail(e.target.value)} /></L>
                          <L label="부서/팀"><I name="organization" autoComplete="organization" value={department} onChange={e=>setDepartment(e.target.value)} /></L>
                          <L label="직급"><I name="organization-title" autoComplete="organization-title" value={position} onChange={e=>setPosition(e.target.value)} /></L>

                        </div>
                      </div>
                    </div>

                    {/* 등록(수정)자 정보 */}
                    {/* <div className="rounded-lg border bg-white">
                      <div className="px-4 py-2 border-b text-sm text-neutral-500">등록(수정)자 정보</div>
                      <div className="p-4">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-3 text-sm">
                          <L label="등록(수정)자">{createdBy || "-"}</L>
                          <L label="등록(수정)일시">{createdAt || "-"}</L>
                        </div>
                      </div>
                    </div> */}

                    <div className="flex gap-2 justify-center">
                      <button
                        type="submit"
                        disabled={saving}
                        className="rounded-lg bg-violet-300 text-violet-900 px-6 py-2 text-sm hover:bg-violet-500 disabled:opacity-50"
                      >
                        {saving ? "저장 중..." : "저장"}
                      </button>
                      <button type="button" onClick={goList} className="rounded-lg border px-6 py-2 text-sm hover:bg-gray-50">
                        이전
                      </button>
                      <button
                        type="button"
                        onClick={() => setOpenDelete(true)}
                        className="ml-auto rounded-lg bg-rose-100 text-rose-700 px-6 py-2 text-sm hover:bg-rose-200"
                      >
                        삭제
                      </button>
                    </div>
                  </form>
                )}

                <ConfirmModal
                  open={openDelete}
                  title="삭제"
                  message="해당 관리자를 삭제하시겠습니까?"
                  onClose={() => setOpenDelete(false)}
                  buttons={[
                    { label: "취소", onClick: () => setOpenDelete(false) },
                    { label: "삭제", intent: "primary", autoFocus: true, onClick: remove },
                  ]}
                />

                <ConfirmModal
                  open={openAlert}
                  title=""
                  message={alertMsg || "처리가 완료되었습니다."}
                  onClose={() => { setOpenAlert(false); goList(); }}
                  buttons={[{ label: "확인", intent: "primary", autoFocus: true, onClick: () => { setOpenAlert(false); goList(); } }]}
                />
              </section>
            </main>
          </div>
        </div>
      </div>
    </div>
  );
};

const L = ({ label, children }) => (
  <div className="flex items-center gap-3">
    <div className="w-28 shrink-0 text-neutral-500">{label}</div>
    <div className="flex-1">{children}</div>
  </div>
);
const I = (props) => <input className="w-full rounded-md border px-3 py-2" {...props} />;

export default AdminDetail;
